import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";

const defaultForm = {
  client_id: "", client_name: "", title: "", description: "",
  status: "draft", issue_date: "", expiration_date: "", invoice_amount: 0, notes: "",
  agreement_sent_date: "", agreement_received_date: "",
};

const statusColors = {
  draft: "bg-gray-100 text-[#6B7280] border-gray-200",
  active: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  pending: "bg-[#FFD100]/10 text-[#946B00] border-[#FFD100]/20",
  expired: "bg-gray-100 text-[#6B7280] border-gray-200",
  cancelled: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
};

const fmt = (n) => (!n || n === 0) ? "$0" : `$${Number(n).toLocaleString()}`;

export default function ClientContracts() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [newClient, setNewClient] = useState({ name: "" });

  useEffect(() => { load(); loadClients(); }, []);
  const load = async () => { try { const { data } = await api.get("/client-contracts"); setItems(data); } catch { toast.error("Failed to load"); } };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const { search, setSearch, sortKey, sortDir, toggleSort, filters, setFilter, clearFilters, processed, activeFilterCount } = useTableFeatures(items);

  const csvCols = [
    { label: "Client", key: "client_name" }, { label: "Title", key: "title" }, { label: "Status", key: "status" },
    { label: "Issue Date", key: "issue_date" },
    { label: "Agreement Sent", key: "agreement_sent_date" },
    { label: "Agreement Received", key: "agreement_received_date" },
    { label: "Expiration", key: "expiration_date" },
    { label: "Invoice Amount", key: "invoice_amount" },
  ];
  const SH = ({ k, children, className = "" }) => (
    <TableHead className={`py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20] ${className}`} onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    try {
      if (editing) { await api.put(`/client-contracts/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/client-contracts", form); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete this contract?")) return; try { await api.delete(`/client-contracts/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  const updateAgreementSent = async (item, date) => {
    // Optimistic UI update
    setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, agreement_sent_date: date } : x));
    try {
      await api.put(`/client-contracts/${item.id}`, { ...item, agreement_sent_date: date });
      toast.success(date ? "Agreement sent date saved" : "Cleared");
    } catch {
      toast.error("Failed to save");
      load();
    }
  };

  const updateAgreementReceived = async (item, date) => {
    setItems((prev) => prev.map((x) => x.id === item.id ? { ...x, agreement_received_date: date } : x));
    try {
      await api.put(`/client-contracts/${item.id}`, { ...item, agreement_received_date: date });
      toast.success(date ? "Agreement received date saved" : "Cleared");
    } catch {
      toast.error("Failed to save");
      load();
    }
  };
  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectClient = (id) => {
    if (id === "__new__") {
      setAddClientOpen(true);
      return;
    }
    const c = clients.find((cl) => cl.id === id);
    setForm((f) => ({ ...f, client_id: id, client_name: c ? c.name : "" }));
  };

  const saveNewClient = async () => {
    if (!newClient.name.trim()) { toast.error("Client name is required"); return; }
    try {
      const { data: client } = await api.post("/clients", { name: newClient.name });
      if (newClient.title?.trim()) {
        await api.post("/client-contracts", {
          client_id: client.id, client_name: client.name, title: newClient.title,
          description: newClient.description || "", status: newClient.status || "draft",
          invoice_amount: newClient.invoice_amount || 0, issue_date: newClient.issue_date || "",
          expiration_date: newClient.expiration_date || "", notes: newClient.notes || "",
        });
        toast.success(`Client "${client.name}" and contract created`);
        load();
      } else {
        toast.success(`Client "${client.name}" created`);
      }
      setAddClientOpen(false);
      setNewClient({ name: "" });
      await loadClients();
      setForm((f) => ({ ...f, client_id: client.id, client_name: client.name }));
    } catch { toast.error("Failed to create"); }
  };

  return (
    <div className="p-6" data-testid="client-contracts-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Management</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Client Contracts</h1>
          <p className="text-sm text-[#6B7280] mt-1">Consulting agreements, engagement letters, and client invoicing</p>
        </div>
        <button data-testid="add-client-contract-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
          <Plus size={16} weight="bold" /> Add Contract
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-client-contracts" placeholder="Search contracts..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger data-testid="filter-cc-status" className="rounded-none w-[130px]"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent className="rounded-none">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-[#002FA7] hover:underline">Clear</button>}
        <button data-testid="export-cc-btn" onClick={() => exportCSV(processed, csvCols, "client_contracts.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <SH k="client_name">Client</SH>
              <SH k="title">Title</SH>
              <SH k="status">Status</SH>
              <SH k="issue_date">Issue Date</SH>
              <SH k="agreement_sent_date">Agreement Sent</SH>
              <SH k="agreement_received_date">Agreement Received</SH>
              <SH k="expiration_date">Expiration</SH>
              <SH k="invoice_amount">Invoice Amount</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-8 text-[#6B7280] text-sm">No client contracts found</TableCell></TableRow>
            ) : processed.map((item) => (
              <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.client_name || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-[#1A1D20]">{item.title}</TableCell>
                <TableCell className="py-3 px-4"><Badge className={`rounded-none text-xs border capitalize ${statusColors[item.status] || statusColors.draft}`}>{item.status}</Badge></TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.issue_date || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">
                  <Input
                    data-testid={`cc-agreement-sent-${item.id}`}
                    type="date"
                    value={item.agreement_sent_date || ""}
                    onChange={(e) => updateAgreementSent(item, e.target.value)}
                    className="rounded-none h-8 text-xs px-2 w-[140px]"
                  />
                </TableCell>
                <TableCell className="py-3 px-4 text-sm">
                  <Input
                    data-testid={`cc-agreement-received-${item.id}`}
                    type="date"
                    value={item.agreement_received_date || ""}
                    onChange={(e) => updateAgreementReceived(item, e.target.value)}
                    className="rounded-none h-8 text-xs px-2 w-[140px]"
                  />
                </TableCell>
                <TableCell className="py-3 px-4 text-sm">{item.expiration_date || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm font-medium text-[#002FA7]">{item.invoice_amount > 0 ? fmt(item.invoice_amount) : "—"}</TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-cc-${item.id}`} onClick={() => edit(item)} className="p-1.5 hover:bg-gray-100"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                    <button data-testid={`delete-cc-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} Client Contract</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client</label>
              <Select value={form.client_id} onValueChange={selectClient}>
                <SelectTrigger data-testid="cc-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="rounded-none max-h-[200px]">
                  <SelectItem value="__new__" className="text-[#002FA7] font-semibold">+ Add New Client</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Title *</label>
              <Input data-testid="cc-title-input" value={form.title} onChange={(e) => set("title", e.target.value)} className="rounded-none" placeholder="e.g. Consulting Agreement FY2026" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Description</label>
              <textarea data-testid="cc-description-input" value={form.description} onChange={(e) => set("description", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger data-testid="cc-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Invoice Amount ($)</label>
                <Input data-testid="cc-invoice-input" type="number" value={form.invoice_amount} onChange={(e) => set("invoice_amount", parseFloat(e.target.value) || 0)} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Issue Date</label>
                <Input data-testid="cc-issue-input" type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Agreement Sent</label>
                <Input data-testid="cc-agreement-sent-input" type="date" value={form.agreement_sent_date} onChange={(e) => set("agreement_sent_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Agreement Received</label>
                <Input data-testid="cc-agreement-received-input" type="date" value={form.agreement_received_date} onChange={(e) => set("agreement_received_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Expiration Date</label>
                <Input data-testid="cc-expiration-input" type="date" value={form.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="cc-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-cc-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-cc-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add New Client Dialog */}
      <Dialog open={addClientOpen} onOpenChange={setAddClientOpen}>
        <DialogContent className="rounded-none sm:max-w-[560px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Add New Client & Contract</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client Name *</label>
              <Input data-testid="new-client-name" value={newClient.name} onChange={(e) => setNewClient((c) => ({ ...c, name: e.target.value }))} className="rounded-none" placeholder="School or organization name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Title</label>
              <Input data-testid="new-contract-title" value={newClient.title || ""} onChange={(e) => setNewClient((c) => ({ ...c, title: e.target.value }))} className="rounded-none" placeholder="e.g. Consulting Agreement FY2026" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Description</label>
              <textarea data-testid="new-contract-description" value={newClient.description || ""} onChange={(e) => setNewClient((c) => ({ ...c, description: e.target.value }))} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
                <Select value={newClient.status || "draft"} onValueChange={(v) => setNewClient((c) => ({ ...c, status: v }))}>
                  <SelectTrigger data-testid="new-contract-status" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Invoice Amount ($)</label>
                <Input data-testid="new-contract-invoice" type="number" value={newClient.invoice_amount || 0} onChange={(e) => setNewClient((c) => ({ ...c, invoice_amount: parseFloat(e.target.value) || 0 }))} className="rounded-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Issue Date</label>
                <Input data-testid="new-contract-issue" type="date" value={newClient.issue_date || ""} onChange={(e) => setNewClient((c) => ({ ...c, issue_date: e.target.value }))} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Expiration Date</label>
                <Input data-testid="new-contract-expiration" type="date" value={newClient.expiration_date || ""} onChange={(e) => setNewClient((c) => ({ ...c, expiration_date: e.target.value }))} className="rounded-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="new-contract-notes" value={newClient.notes || ""} onChange={(e) => setNewClient((c) => ({ ...c, notes: e.target.value }))} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setAddClientOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-new-client-btn" onClick={saveNewClient} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Create Client & Contract</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
